//@ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Paperclip, X } from "lucide-react";
import CustomMenuDropdown from "../../common/CustomMenuDropdown";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { CrossIcon } from "../../icons/Icons";
import { createAppScoreCardDataThunk, fetchScorecardByListDataFilter } from "../../../features/ApplicationData/ApplicationDataSlice";
import { deleteAppScorecardDetails, fetchScoreCardPipelineDetailsData } from "../../../features/scorecard/scoreCardSlice";
import { motion, AnimatePresence } from "framer-motion";
import { fetchAppSkillRatingByDetails } from "../../../features/AppSkillRating/AppSkillRatingSlice";
import { fetchApplicationQuestionnaireByListDataFilter } from "../../../features/applicationQuestionnaire/applicationQuestionnaireSlice";
import { fetchJobPipelineDetailsData } from "../../../features/pipeline/pipelineSlice";
import { formatDistanceToNow } from "date-fns";
import { useSelector } from "react-redux";


// Function to determine badge color based on skill scale value
const getSkillScaleBadgeStyles = (scale: any) => {

  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10"
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};

// Function to get skill level label based on scale value
const getSkillLevelLabel = (scale: any) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Expert";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Proficient";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Competent";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "Limited";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Inadequate";

  return null;
};


// Function to determine rating color
const getRatingColor = (rating: any) => {
  if (!rating) return '#9ca3af'; // Gray for null ratings
  const score = parseFloat(rating);
  if (score >= 4) return '#10b981'; // Green for high ratings
  if (score >= 2.5) return '#f59e0b'; // Yellow/orange for medium
  return '#ef4444'; // Red for low ratings
};



const getRatingLabel = (rating: any) => {
  if (!rating) return null;

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) return "Strong Yes";
  if (ratingValue === 4) return "Yes";
  if (ratingValue === 3) return "Neutral";
  if (ratingValue === 2) return "No";
  if (ratingValue === 1) return "Strong No";

  return null;
};


// Function to get badge styles based on rating value
const getRatingBadgeStyles = (rating: any) => {
  if (!rating) return {
    bg: "bg-gray-50",
    text: "text-gray-700",
    ring: "ring-gray-700/10"
  };

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) {
    // Strong Yes: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (ratingValue === 4) {
    // Yes: Light Green
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-700/10"
    };
  } else if (ratingValue === 3) {
    // Neutral: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (ratingValue === 2) {
    // No: Orange
    return {
      bg: "bg-orange-50",
      text: "text-orange-700",
      ring: "ring-orange-700/10"
    };
  } else if (ratingValue === 1) {
    // Strong No: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};



// Individual circular progress component
const CircularRatingProgress = ({ rating, maxRating = 5, size = 40, strokeWidth = 5, color = '#3b82f6' }) => {
  // Handle null or undefined ratings
  const actualRating = rating ? parseFloat(rating) : 0;

  // Calculate percentage
  const percentage = (actualRating / maxRating) * 100;

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Center position
  const center = size / 2;

  return (
    <div className="flex items-center justify-center bg-white rounded-lg">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>

        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-bold" style={{ color }}>{rating || '-'}</span>
          {/* <span className="text-xs text-gray-500">Score</span> */}
        </div>
      </div>
    </div>
  );
};


const ratingOptions1 = [
  { value: 1, label: "Strong No" },
  { value: 2, label: "No" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Yes" },
  { value: 5, label: "Strong Yes" },
];


const RatingButton1 = ({ label, value, selectedValue, onClick }) => {
  const isSelected = selectedValue == value;

  return (
    <button
      className={`flex items-center gap-[6px] text-[13px] rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${isSelected
        ? 'bg-indigo-600 text-white ring-1 ring-inset ring-indigo-600'
        : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      onClick={() => onClick(value)}
      type="button"
    >
      {label}
    </button>
  );
};


function ReviewQuestionnaireSection({ appreviewId, app: appProp }) {
  const [scorecard, setScorecard] = useState([]);
  const [loading, setLoading] = useState(true);
  // const { baseId, jobId, applicationId, skillId } = useParams<{ baseId: string; jobId: string; applicationId: string; skillId: string }>();
  // const { baseId, jobId, applicationId } = useParams<{ baseId: string; jobId: string; applicationId: string }>();
  const { baseId, jobId, applicationId: applicationIdFromParams, skillId } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
    skillId: string
  }>();

  const applicationId = appProp || applicationIdFromParams
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
   const userDetailsData = localStorage.getItem('jobByHiringTeamMembers');
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const [pipelineStages, setPipelineStages] = useState([]);
  // const [openAccordionIds, setOpenAccordionIds] = useState({});
  const [openAccordionIds, setOpenAccordionIds] = useState(() => {
    // Create an object with all accordion IDs set to true
    const initialState = {};
    // If you have access to the scorecard data at this point:
    if (scorecard && scorecard.skill) {
      scorecard.skill.forEach(item => {
        initialState[item.id] = true;
      });
    }
    return initialState;
  });
  const navigate = useNavigate()
  const [showSkills, setShowSkills] = useState(false)
  const [openAccordionIds1, setOpenAccordionIds1] = useState({});
  const [appScorecardData, setAppScorecardData] = useState([])
  // const [loadingSkillRatings, setLoadingSkillRatings] = useState({});
  const [loadingAppScorecards, setLoadingAppScorecards] = useState({});
  const [openSkillAccordionIds, setOpenSkillAccordionIds] = useState({});
  const [openDeleteModalExp, setOpenDeleteModalExp] = useState(false)
  const [deleteLoadingExp, setDeleteLoadingExp] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [openAccordionIds1Candidate, setOpenAccordionIds1Candidate] = useState({});
  const [overallRating, setOverallRating] = useState(null);
  const [overallComment, setOverallComment] = useState("");
  const [appScorecardCandidateOpen, setAppScorecardCandidateOpen] = useState(false)
  const [overallRatingError, setOverallRatingError] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [openAccordionIds1CandidateCreate, setOpenAccordionIds1CandidateCreate] = useState({});
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [openDeleteModalExpCandidate, setOpenDeleteModalExpCandidate] = useState(false)
  const [deleteLoadingExpCandidate, setDeleteLoadingExpCandidate] = useState(false)
  const [selectedIndexCandidate, setSelectedIndexCandidate] = useState(null);

  const getStageName = (stageId: any) => {
    // Get scorecard data from localStorage
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");

    // Check if scorecardData has the correct structure
    const stages = scorecardData.stages || [];

    // Find the stage with matching ID
    const stage = stages.find(stage => stage.id == stageId);

    return stage ? stage.stage_name : null;
  };

  // Function to get user name by ID
  const getUserName = (userId: any) => {
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.name : `User ${userId}`;
  };

  // Function to render rating progress bar
  const renderRatingProgressBar = (rating: any) => {
    if (!rating) return null;

    // Assuming rating is on a scale of 1-5
    const maxRating = 5;
    const percentage = (rating / maxRating) * 100;

    // Determine color based on rating
    let barColor = "#3b82f6"; // Default blue
    if (percentage >= 80) barColor = "#10b981"; // Green for high ratings
    else if (percentage >= 50) barColor = "#f59e0b"; // Yellow/orange for medium
    else barColor = "#ef4444"; // Red for low ratings

    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div
          className="h-2 rounded-full"
          style={{
            width: `${percentage}%`,
            backgroundColor: barColor
          }}
        ></div>
        <div className="text-xs text-gray-500 mt-1">{rating} out of {maxRating}</div>
      </div>
    );
  };


  useEffect(() => {
    if (baseId) {
      const fetchJob = async () => {
        // Try to get pipeline stages from localStorage
        const storedBase = localStorage.getItem("jobPipelineStageData");
        if (storedBase) {
          try {
            const parsedData = JSON.parse(storedBase);
            if (parsedData && parsedData.data && parsedData.data.stages) {
              setPipelineStages(parsedData.data.stages);
            }
          } catch (error) {
            console.error("Error parsing local storage data:", error);
          }
        } else {
          try {
            const res = await dispatch(
              fetchScoreCardPipelineDetailsData({
                baseId: Number(baseId),
                JobId: jobId
              })
            );
            if (res && res.data) {
              setPipelineStages(res?.data?.stages);
            }
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchJob();
    }
  }, [baseId, dispatch, jobId]);


  useEffect(() => {
    const fetchScorecard = async () => {
      if (baseId && applicationId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchApplicationQuestionnaireByListDataFilter({
              baseId: Number(baseId),
              appId: applicationId,
              page: page,
              limit: itemsPerPage,
            })
          ).unwrap();
          if (res.status === 200) {
            setScorecard(res.data);

            // Initialize openAccordionIds with all scorecard IDs set to true
            const initialOpenState = res.data.reduce((acc, scorecard) => {
              acc[scorecard.id] = true;
              return acc;
            }, {});
            setOpenAccordionIds(initialOpenState);
          }
        } catch (error) {
          console.error("Error fetching scorecard:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchScorecard();
  }, [baseId, applicationId, dispatch, page, itemsPerPage]);


  // Function to handle accordion toggle
  const handleAccordionOpen = (id: any) => {
    setOpenAccordionIds(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleCreateSkillOpen = (id: any) => {
    navigate(`/${baseId}/job/${jobId}/app/${applicationId}/questionnaire/${id}`)
  }

  const handleEditClick = (appScorecard: any) => {
    navigate(`/${baseId}/job/${jobId}/app/${applicationId}/scorecards/${appScorecard.id}/edit`)
  }

  const handleDeleteClick = (appScoreCardId: any) => {
    setOpenDeleteModalExp(true)
    setSelectedIndex(appScoreCardId)
  }

  // Function to handle cancel button in delete modal
  const handleCloseModal = () => {
    setOpenDeleteModalExp(false);
  };

  const handleDeleteConfirm = async (appScorecard: any) => {

    setDeleteLoadingExp(true); // Start loading

    try {
      await dispatch(
        deleteAppScorecardDetails({
          baseId: Number(baseId),
          id: selectedIndex?.id,
        })
      ).unwrap();

      toast.success("App scorecard deleted successfully!");
      setOpenDeleteModalExp(false);
      // setSelectedFile(null)


      const res = await dispatch(
        fetchScorecardByListDataFilter({
          baseId: Number(baseId),
          appId: applicationId
        })
      ).unwrap();

      if (res.status === 200) {
        setScorecard(res.data);
      }
    } catch (error) {
      toast.error("Failed to delete app scorecard!");
    } finally {
      setDeleteLoadingExp(false); // Stop loading
    }
  };



  const handleAppSkillRating = (id, data) => {
    // Check if this accordion is already open
    const isCurrentlyOpen = openAccordionIds1[id] || false;

    // Close all accordions and only open the current one if it wasn't already open
    setOpenAccordionIds1((prev) => {
      // Start with an empty object (all accordions closed)
      const newState = {};

      // Only set the current accordion to open if it was previously closed
      if (!isCurrentlyOpen) {
        newState[id] = true;

        // Only fetch data if we're opening this accordion
        setLoadingAppScorecards(prev => ({
          ...prev,
          [id]: true
        }));

        if (baseId && applicationId && id) {
          dispatch(fetchAppSkillRatingByDetails({
            baseId: Number(baseId),
            appId: applicationId,
            appScoreId: id
          }))
            .unwrap()
            .then((response) => {
              if (response.status === 200) {
                setAppScorecardData(response.data.skillReting);
              }
            })
            .catch((error) => {
              console.error(`Error fetching skill rating details:`, error);
            })
            .finally(() => {
              setLoadingAppScorecards(prev => ({
                ...prev,
                [id]: false
              }));
            });
        }
      }

      return newState;
    });
  }


  const handleOverallRatingSelect = (value) => {
    setOverallRating(value);
  };

  // Handle overall comment change
  const handleOverallCommentChange = (e) => {
    setOverallComment(e.target.value);
    // if (overallCommentError) {
    //   setOverallCommentError(false);
    // }
  };

  const [pipelineStageData, setPipelineStageData] = useState([])



  useEffect(() => {
    if (baseId) {
      const fetchData1 = async () => {

        const storedBase = localStorage.getItem('jobPipelineStageData');
        if (storedBase) {
          const teamInfo = JSON.parse(storedBase);
          setPipelineStageData(teamInfo.stages)
        } else {
          try {
            const res = await dispatch(fetchJobPipelineDetailsData({ baseId: Number(baseId), JobId: jobId })).unwrap()
            setPipelineStageData(res?.data.stages)
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchData1();
    }
  }, [baseId, dispatch]);




  return (
    <div className="w-full max-w-[640px] m-auto">
      <span className="text-sm text-[#374151] font-medium leading-4 capitalize mb-3 block">
        questionnaire
      </span>

      <div className="space-y-12">
        {loading ? (
          <div className="flex items-center justify-center relative z-50" style={{ height: "100px" }}>
            <div className="simple-spinner-loader">
              <span></span>
            </div>
          </div>
        ) : scorecard.length === 0 ? (
          <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              <path d="M12 9h.01" />
              <path d="M11 12h1v4h1" />
            </svg>
            <p className="text-[#0000007a] text-[13px]">No results found</p>
          </div>

        ) : (
          [...scorecard].map((msg, index) => {


            const stageId = msg?.appScorecards?.find(item => item.stage_id)?.stage_id || msg.stage_id;

            const assignedToIds = msg.assigned_to ? msg.assigned_to.split(',') : [];
            const assignedUsers = assignedToIds.map(id => getUserName(id));

            const isOpen = openAccordionIds[msg.id] || false;

            const isSkillOpen = openSkillAccordionIds[msg.id] || false;

            // stages
            const senderStages = pipelineStageData.find(member => member.id == msg.stage_id) || {};
            const senderStageName = senderStages.stage_name;



            // user
            const senderUser = baseMembers.find(member => member.user_id == msg.assign_to) || {};
            const senderName = senderUser.name;


            return (
              <>
                <div className="rounded-md cursor-pointer ring-1 ring-gray-200 feedback-delete-hover">
                  <div className="flex items-start">
                    <div className="w-[80%] flex flex-col gap-1 px-4 pt-[15px] pb-[15px]">
                      <div className="flex justify-start items-center gap-x-2">
                        <span className="flex items-center gap-x-3">
                          <span className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                            style={{ backgroundColor: msg.questionset_colour }}
                          >

                          </span>
                        </span>
                        <div className="flex gap-x-[6px] items-center">
                          <span className="text-sm text-[#374151] font-medium leading-4 ">
                            {
                              msg.questionset_name
                            }
                          </span>

                        </div>
                      </div>
                      <p className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4">
                        {msg.questionset_description}
                      </p>
                      <div className="relative mt-1 flex items-center gap-x-1">
                        {
                          senderName &&
                          <span className="inline-flex capitalize items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                            User: {senderName}
                          </span>
                        }
                        {
                          senderStageName &&
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                            Stage:  {senderStageName}
                          </span>
                        }
                        {
                          msg?.scorecard_name &&
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                            Scorecard:  {msg?.scorecard_name}
                          </span>
                        }

                      </div>
                    </div>

                    <div className="w-[20%] pr-4 mr-2 pt-[15px] flex items-center gap-x-2 justify-end">
                      {/* <span className="table-tooltip before:!bottom-[28px] before:!left-[24px]"
                        data-title='skills'>
                        <button

                          onClick={() => handleAccordionOpen(msg.id)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                          outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                          [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">

<motion.div
                            // animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                         <div className="w-full flex items-center gap-x-2">
                         Skills
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={16}
                              height={16}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                         </div>
                          </motion.div>
                        </button>
                      </span> */}

                      {/* <div className="data-title"
>
<p className="font-normal text-[#374151] text-[14px] wg-antialiased leading-4">
                        {msg.questionset_description}
                      </p>
</div> */}

                    </div>
                  </div>
                  {
                    msg?.appQuestionSets.length > 0 &&
                    <>
                      <div className="bg-[#e0e0e0] w-full h-[1px] mb-0"></div>
                      <div className="w-full">
                        <div className="px-4 py-2 flex items-center justify-between">
                          <div className="w-full">
                            {msg?.appQuestionSets[0]?.status === "pending" && (
                              <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                                Candidate pending
                              </span>
                            )}

                            {msg?.appQuestionSets[0]?.status === "done" && msg?.appQuestionSets[0]?.review_status === "pending" && (
                              <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                                Review Pending
                              </span>
                            )}

                            {msg?.appQuestionSets[0]?.status === "done" && msg?.appQuestionSets[0]?.review_status === "done" && (
                              <div className="flex items-center justify-between w-full">
                                <div className="flex flex-col gap-y-1">
                                  {msg?.appQuestionSets[0]?.assigned_to && (
                                    <span className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                      {getUserName(msg?.appQuestionSets[0]?.assigned_to)}
                                    </span>
                                  )}

                                  {msg?.appQuestionSets[0]?.comment && (
                                    <span className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                      "{msg?.appQuestionSets[0]?.comment}"
                                    </span>
                                  )}
                                </div>
                                <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-normal text-green-700 ring-1 ring-inset ring-green-700/10">
                                  {msg?.appQuestionSets[0]?.rating}
                                </span>
                              </div>
                            )}


                          </div>
                          {
                            msg?.appQuestionSets[0]?.status === "pending" &&
                            <span className="table-tooltip before:!bottom-[28px] before:!left-[41px]"
                              data-title='questionnaire'>
                              <button

                                onClick={() => handleCreateSkillOpen(msg?.appQuestionSets[0]?.id)}
                                className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
      outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
      [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">

                                Questionnaire
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width={24}
                                  height={24}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-right w-[0.9rem] h-[0.9rem] text-[#808080]"
                                >
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M5 12l14 0" />
                                  <path d="M15 16l4 -4" />
                                  <path d="M15 8l4 4" />
                                </svg>


                              </button>
                            </span>
                          }

                          {msg?.appQuestionSets[0]?.status === "done" && msg?.appQuestionSets[0]?.review_status === "done" && msg?.appQuestionSets[0]?.submitted_at && (
                            <div className="">
                              {formatDistanceToNow(new Date(msg?.appQuestionSets[0]?.submitted_at), { addSuffix: true, includeSeconds: true })
                                .replace("about ", "")
                                .replace("less than ", "")
                                .replace("half a ", "1 ")}
                            </div>
                          )}

                        </div>

                      </div>
                    </>

                  }

                  {/* <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>
                        {msg.skills && msg.skills.length > 0 && (
                          <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">Skills</span>
                        )}
                        {msg.skills && msg.skills.length > 0 ? (
                          <div className="w-full p-4 space-y-2">
                            {msg.skills.map((skill, idx) => {

                              const styles = getSkillScaleBadgeStyles(skill.skill_value);

                              return (
                                <div className="flex flex-col gap-y-2" key={idx}>
                                  <div className="p-2 rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover 
                                  ">
                                    <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                                      <div className="flex items-center gap-x-[16px]">
                                        <div className=" shrink-0 w-[9px] h-[9px] rounded-full" style={{ background: skill.skill_colour }} />
                                        <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-5 capitalize">
                                          {skill.skill_name}
                                        </span>

                                        <div className=" cursor-default select-none flex flex-row" />
                                      </div>
                                      {
                                        skill.skill_value &&
                                        <span
                                          // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                          className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                        >
                                          {getSkillLevelLabel(skill.skill_value)}
                                        </span>
                                      }

                                    </div>
                                  </div>
                                </div>
                              )

                            })}
                          </div>
                        ) : <span className="text-[#0000007a] text-[13px] text-center block  py-4">Nothing here</span>}

                      </motion.div>
                    )}
                  </AnimatePresence> */}


                  {msg.appScorecards && msg.appScorecards.length > 0 && (
                    <>
                      <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>

                      <div className="w-full">
                        {msg.appScorecards.map((appScorecard, idx) => {
                          const reviewerName = getUserName(appScorecard.assigned_to);
                          const appStageName = appScorecard.stage_id ? getStageName(appScorecard.stage_id) : stageName;
                          const styles1 = getRatingBadgeStyles(appScorecard.rating);
                          const isAccordionOpen = openAccordionIds1[appScorecard.id] || false;


                          const dropdownGroups = [
                            {
                              options: [
                                {
                                  label: `Edit`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleEditClick(appScorecard),
                                },
                                {
                                  label: `Delete`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleDeleteClick(appScorecard),

                                },
                              ],
                            },
                          ];

                          return (
                            <div key={idx} className="flex items-center gap-x-2 border-b border-light-gray-200 last-of-type:border-b-0">
                              <div key={appScorecard.id || idx} className="w-full">
                                <ul className="list-none w-full  px-4">
                                  <li className="flex flex-row items-start justify-between text-[0.875rem] px-[0rem] py-4 w-full">
                                    {/* <div className="flex shrink-0 pr-2">
                                      <div
                                        className="h-[40px] w-[40px] rounded-[8px] flex flex-row items-center justify-center text-white"

                                      >
                                        <CircularRatingProgress
                                          rating={appScorecard.rating}
                                          color={getRatingColor(appScorecard.rating)}
                                        />
                                      </div>
                                    </div> */}
                                    <div className="flex-1 px-0 flex flex-col gap-y-[4px]">
                                      <div className="flex items-center gap-x-2 mt-[-2px]">
                                        <p className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                          {reviewerName}
                                        </p>

                                      </div>
                                      <span className="inline-flex w-fit items-center rounded-md px-1 py-1 text-xs font-medium bg-gray-50 text-gray-600 ring-gray-500/10 capitalize ring-1 ring-inset ">
                                        {appStageName}
                                      </span>
                                      {
                                        appScorecard.comment &&
                                        <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                          "{appScorecard.comment}"
                                        </p>
                                      }


                                    </div>
                                    <div className="flex gap-1 items-center">

                                      {
                                        appScorecard.rating &&
                                        <span
                                          // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                          className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                        >
                                          {getRatingLabel(appScorecard.rating)}
                                        </span>
                                      }



                                      {
                                        appScorecard.status === 'pending' &&
                                        <span
                                          onClick={() => handleCreateSkillOpen(msg.id)}
                                          className="inline-flex mt-[0px] w-fit items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize"
                                        >
                                          {appScorecard.status}
                                        </span>

                                      }
                                      <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                                        data-title='skills'>
                                        <button
                                          onClick={() => handleAppSkillRating(appScorecard.id, msg)}
                                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                                          <motion.div
                                            animate={{ rotate: isAccordionOpen ? 180 : 0 }}

                                            transition={{ duration: 0.3 }}
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width={16}
                                              height={16}
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                                            >
                                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                              <path d="M6 9l6 6l6 -6" />
                                            </svg>

                                          </motion.div>
                                        </button>

                                      </span>
                                      <div className='ml-[-4px]'>
                                        <div className="[&>div]:shrink">
                                          <CustomMenuDropdown
                                            sections={dropdownGroups}
                                            icon={
                                              <svg
                                                stroke="currentColor"
                                                fill="none"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                                focusable="false"
                                                height="1em"
                                                width="1em"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <circle cx="12" cy="12" r="1"></circle>
                                                <circle cx="12" cy="5" r="1"></circle>
                                                <circle cx="12" cy="19" r="1"></circle>
                                              </svg>
                                            }
                                            menuItemsClassName="left-auto"
                                          />



                                        </div>
                                      </div>
                                    </div>

                                  </li>

                                </ul>
                                <AnimatePresence>
                                  {isAccordionOpen && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                                      {loadingAppScorecards[appScorecard?.id] ? (
                                        <div className="flex items-center justify-center py-4">
                                          <div className="simple-spinner-loader">
                                            <span></span>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          {appScorecardData && appScorecardData.length > 0 && (
                                            <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">
                                              Skills
                                            </span>
                                          )}

                                          {appScorecardData && appScorecardData.length > 0 ? (
                                            <div className="w-full p-4 space-y-2">
                                              {appScorecardData.map((skill, idx) => {
                                                const styles1 = getRatingBadgeStyles(skill.skill_scale);

                                                return (
                                                  <div className="flex flex-col gap-y-2" key={idx}>
                                                    <div className="p-2 flex items-center rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover">
                                                      <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                                                        <div className="flex items-start gap-x-[8px]">
                                                          <div
                                                            className="h-[8px] w-[8px] rounded-[2px] mt-[4px]"
                                                            style={{ background: skill.skill_colour }}
                                                          />
                                                          <div className="flex flex-col gap-y-1">
                                                            <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-4 capitalize">
                                                              {skill.skill_name}
                                                            </span>
                                                            {
                                                              skill.comment &&
                                                              <span className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                                                "{skill.comment}"
                                                              </span>
                                                            }


                                                          </div>
                                                          <div className="cursor-default select-none flex flex-row" />

                                                        </div>
                                                      </div>
                                                      {
                                                        skill.skill_scale &&
                                                        <span
                                                          // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                                          className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                                        >
                                                          {getRatingLabel(skill.skill_scale)}
                                                        </span>
                                                      }
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <span className="text-[#0000007a] text-[13px] text-center block py-4">
                                              Nothing here
                                            </span>
                                          )}
                                        </>
                                      )}


                                    </motion.div>
                                  )}
                                </AnimatePresence>

                              </div>

                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>

            );
          })
        )}
      </div>


      {/* skill */}

      {openDeleteModalExp && (
        <Dialog open={true} onClose={handleCloseModal} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <CrossIcon />
                  </button>
                </div>
                <div className="flex flex-col gay-y-4">
                  <span className="leading-6 text-sm text-gray-500">
                    Do you want to sure delete this app scorecard?
                  </span>
                </div>
                <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="items-center px-[14px] py-1 text-[13px] font-medium h-[32px] inline-flex leading-6 w-fit justify-center rounded-md bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    type="button"
                    className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
                  >
                    {deleteLoadingExp ? (
                      <div className='flex items-center gap-[6px]'>
                        Delete
                        <div className="simple-spinner">
                          <span></span>
                        </div>
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}


    </div>
  );
}

export default ReviewQuestionnaireSection;